import React, { cloneElement, useMemo } from 'react';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import FuseUtils from '@fuse/utils';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import * as Actions from 'app/store/actions';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { ListItemIcon } from '@material-ui/core';
import { UserService } from 'app/services/userService';
import userRoles from 'app/userRoles';
import { withRouter } from 'utils/withRouter';
import FuseNavBadge from '../FuseNavBadge';

const useStyles = makeStyles(theme => ({
  item: props => ({
    height: 40,
    width: 'calc(100% - 16px)',
    borderRadius: '0 20px 20px 0',
    paddingRight: 12,
    paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
    '&.active': {
      color: `${theme.palette.primary.main}`,
      pointerEvents: 'none',
      transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
      '& .list-item-text-primary': {
        color: 'inherit'
      },
      '& .list-item-icon': {
        color: 'inherit'
      },
      '& div': {
        color: `${theme.palette.primary.main}!important`
      }
    },
    '& .list-item-icon': {
      marginRight: 16
    },
    '& .list-item-text': {},
    color: theme.palette.text.primary,
    cursor: 'pointer',
    textDecoration: 'none!important'
  })
}));

function FuseNavVerticalItem(props) {
  const user = useSelector(({ auth }) => auth.user);
  const dispatch = useDispatch();

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { item, nestedLevel } = props;
  const classes = useStyles({
    itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24
  });
  const { t } = useTranslation('navigation');

  const hasPermission = useMemo(
    () => FuseUtils.hasPermission(item.auth, user.role),
    [item.auth, user]
  );

  const hasOptionalPermission = useMemo(
    () =>
      FuseUtils.hasOptionalItemPermission(
        item.group,
        item.allowedHairdresserTypes,
        user
      ),
    [item.group, item.allowedHairdresserTypes, user]
  );

  if (!hasPermission || !hasOptionalPermission) {
    return null;
  }

  if (!UserService.hasSomeRoles(user, [userRoles.ROLE_SUPER_ADMIN])) {
    if (
      item.allowedCountries &&
      !user.countries.some(country => item.allowedCountries.indexOf(country) >= 0)
    ) {
      return null;
    }
  }

  // Check if the item has a property called 'allowedBrands'
  if (item?.allowedBrands) {
    // Search for a match between the user's brands and the item's allowed brands
    let findBrand = false;

    if (user.hairdresserSelected) {
      findBrand = item?.allowedBrands.includes(
        user?.hairdresserSelected?.brand_id
      );
    } else {
      findBrand = user?.brands.find(el =>
        item?.allowedBrands?.find(brand => brand === el)
      );
    }

    // If no matching brand is found, return false
    if (!findBrand) {
      return false;
    }
  }

  // If selectedBrandUrl return key from user selected code
  function getValueByKey(obj, key) {
    if (obj[key]) {
      return obj[key];
    }
    return item?.url;
  }

  return (
    <ListItem
      button
      component={NavLinkAdapter}
      to={
        item?.selectedBrandUrl
          ? getValueByKey(
              item?.selectedBrandUrl,
              user?.hairdresserSelected?.brands?.code
            )
          : item.url
      }
      activeClassName="active"
      className={clsx(classes.item, 'list-item')}
      onClick={ev => mdDown && dispatch(Actions.navbarCloseMobile())}
      exact={item.exact}
      target={item.url.substring(0, 4) === 'http' ? '_blank' : '_self'}
    >
      {item.icon && (
        <ListItemIcon>
          {cloneElement(item.icon, {
            ref: node => {
              // Call the original ref, if any
              const { ref } = item.icon;
              if (typeof ref === 'function') {
                ref(node);
              }
            },
            // color: 'action',
            className: 'list-item-icon text-30 flex-shrink-0'
          })}
        </ListItemIcon>
      )}

      <ListItemText
        className="list-item-text"
        primary={item.translate ? t(item.translate) : item.title}
        classes={{ primary: 'text-14 list-item-text-primary' }}
      />

      {item.badge && <FuseNavBadge badge={item.badge} />}
    </ListItem>
  );
}

FuseNavVerticalItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.any,
    url: PropTypes.string
  })
};

FuseNavVerticalItem.defaultProps = {};

const NavVerticalItem = withRouter(React.memo(FuseNavVerticalItem));

export default NavVerticalItem;
